import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { AssetDto, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { UploadObj } from '../../../classes/upload-obj.class';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { calcLegacyTheme } from '../../../gallery/lib/calc-legacy-theme';
import { GallerySettingsMenuComponent } from '../../../gallery/menus/gallery-settings-menu/gallery-settings-menu.component';
import { GROWTHBOOK } from '../../../growthbook';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { changeCoverAsset } from '../../../lib/change-cover-asset';
import { fitImageIntoAspectRatio } from '../../../lib/get-and-resize-image-file';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { DialogService } from '../../../services/dialog.service';
import { DownloadAssetService } from '../../../services/download-asset.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { GuestAuthService } from '../../../services/guest-auth.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { UploadService } from '../../../services/upload.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { GuestProfileDialogComponent } from '../../../view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { CreateCdnLinkDialogComponent } from '../../dialogs/create-cdn-link-dialog/create-cdn-link-dialog.component';
import { MoveAssetsDialogComponent } from '../../dialogs/move-assets-dialog/move-assets-dialog.component';

@Component({
  selector: 'app-asset-context-menu',
  templateUrl: './asset-context-menu.component.html',
  styleUrls: ['./asset-context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    SvgComponent,
    ButtonMenuItemComponent,
    UpgradeBadgeComponent,
    BillingScopePipe,
    TranslatePipe,
    AssetDownloadButtonComponent,
    SwitchComponent,
  ],
})
export class AssetContextMenuComponent {
  private state = inject(StateService);
  private growthBook = inject(GROWTHBOOK);
  private assetService = inject(AssetService);
  private downloadAssetService = inject(DownloadAssetService);
  private dialog = inject(DialogService);
  private galleryService = inject(GalleryService);
  private galleryThemeService = inject(GalleryThemeService);
  private toasts = inject(ToastService);
  private uploadService = inject(UploadService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private browserService = inject(BrowserService);
  private guestAuth = inject(GuestAuthService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(PICFLOW_CHECKOUT);

  @ViewChild('versionUpload') versionUploadEl: ElementRef;
  @ViewChild('posterUpload') posterUploadEl: ElementRef;
  readonly clientEl = input<HTMLElement>(undefined);
  readonly asset = input<AssetDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly autoplayShown = input<boolean>(undefined);
  onClose = output();
  onDelete = output({});
  onCoverChange = output();
  openShare = output();
  onNewVersion = output();
  onToggleThumbnails = output();
  canDisplayAsset = computed(() => canDisplayAsset(this.asset()));
  allowDownload = computed(() => {
    const galleryPermissions = this.galleryPermissions();
    const gallery = this.gallery();
    const asset = this.asset();
    if (!gallery || !asset || !galleryPermissions) return false;
    if (galleryPermissions.can_edit) return true;
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  });
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return this.gallery().theme || calcLegacyTheme(this.gallery());
  });

  downloadAsset() {
    this.downloadAssetService.downloadAssetVersion(
      this.asset().version_data,
      this.asset().type,
      this.gallery().id,
      this.asset().watermark_enabled
    );
  }

  async setAsCover() {
    const gallery = this.gallery();
    changeCoverAsset(gallery.cover, this.asset());
    gallery.cover.position = '50% 50%';
    const resp = await this.galleryService.updateGallery(gallery.id, {
      cover: gallery.cover,
      teaser: gallery.cover,
      theme:
        this.theme().cover.design === null
          ? this.galleryThemeService.getCoverDesignChangeParams(this.theme(), 'simple')
          : undefined,
    });
    gallery.cover = resp.cover;
    this.toasts.add('Cover updated', 'success');
    this.onCoverChange.emit();
    this.onClose.emit();
  }

  async deleteAsset() {
    await this.assetService.deleteAsset(this.asset());
    this.onDelete.emit();
    this.analytics.track('asset_deleted', true);
  }

  async uploadVersion(_files: File[]) {
    const files = Array.from(_files);
    if (!files.length) return;
    if (files.length > 1) return this.toasts.add('Please upload only one asset', 'error');
    const file = files[0];
    const resp = await this.uploadService.handleCanUpload(files);
    if (!resp) return;

    await this.uploadService.uploadNewVersion(this.asset(), file);
    await this.uploadService.waitForAssetProcessing(this.asset().id);
    this.onNewVersion.emit();
    this.onClose.emit();
  }

  uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadVersion(Array.from(files));
    this.versionUploadEl.nativeElement.value = '';
  }

  async openGuestProfileDialog() {
    if (this.state.user$.value) {
      location.href = this.browserService.getCorrectPicflowUrl() + '/a/settings/profile';
      return;
    }
    if (!this.state.guestAuthToken) {
      await this.guestAuth.authenticateWithPopup(this.gallery());
    } else {
      this.dialog.open(GuestProfileDialogComponent, { gallery: this.gallery() });
    }
  }

  moveAsset() {
    this.dialog.open(MoveAssetsDialogComponent, { assets: [this.asset()], gallery: this.gallery() });
  }

  async toggleWatermark() {
    const asset = this.asset();
    asset.watermark_enabled = !asset.watermark_enabled;
    this.assetService.updateAsset(
      asset,
      { watermark_enabled: asset.watermark_enabled },
      this.gallery().id,
      <string>asset.section
    );
  }

  openWatermarkingSettings() {
    if (this.permissionService.canDisplayElement(billingScopes.asset.watermarking)) {
      return this.dialog.open(GallerySettingsMenuComponent, { gallery: this.gallery() });
    }
    const tenantId = (this.gallery().tenant as TenantDto)?.id || (this.gallery().tenant as string);
    if (this.permissionService.shouldRedirectToPricingPage(tenantId, this.state.activeTenant$?.value)) {
      this.checkoutService.open(billingScopes.asset.watermarking);
    }
  }

  createCdnLink() {
    if (!this.permissionService.canDisplayElement(billingScopes.asset.createCdnLink)) {
      const tenantId = (this.gallery().tenant as TenantDto)?.id || (this.gallery().tenant as string);
      if (this.permissionService.shouldRedirectToPricingPage(tenantId, this.state.activeTenant$?.value)) {
        this.checkoutService.open(billingScopes.asset.createCdnLink);
      }
      return;
    }
    this.dialog.open(CreateCdnLinkDialogComponent, { asset: this.asset(), gallery: this.gallery() });
  }

  async uploadCustomPoster(target: EventTarget) {
    if (!this.permissionService.canDisplayElement(billingScopes.asset.customVideoPoster)) {
      const tenantId = (this.gallery().tenant as TenantDto)?.id || (this.gallery().tenant as string);
      if (this.permissionService.shouldRedirectToPricingPage(tenantId, this.state.activeTenant$?.value)) {
        this.checkoutService.open(billingScopes.asset.customVideoPoster);
      }
      return;
    }
    const asset = this.asset();
    asset['tempUploadingPoster'] = true;
    const file = (target as HTMLInputElement).files[0];
    const src = URL.createObjectURL(file);

    const aspectRatio = asset.version_data.data.width / asset.version_data.data.height;

    const resizedImageSrc = await fitImageIntoAspectRatio(src, aspectRatio, 0.9);
    URL.revokeObjectURL(src);

    const blob = await fetch(resizedImageSrc).then(r => r.blob());
    const resizedFile = new File([blob], `image.jpg`, { type: `image/jpeg` });

    const upload = <UploadObj>{
      file: resizedFile,
      status: 'started',
      galleryId: this.gallery().id,
    };

    this.uploadService.uploads.push(upload);
    this.uploadService.markUploadAsUploading(upload);

    this.onClose.emit();

    const assetVersion = await this.assetService.updateAssetVersion(asset.version_data, {
      poster: {
        content_length: resizedFile.size,
        content_type: resizedFile.type,
        upload_type: 'put',
      },
    });

    await firstValueFrom(this.uploadService.uploadAutoMethod(assetVersion['poster_upload'], upload));

    URL.revokeObjectURL(resizedImageSrc);

    this.uploadService.markUploadAsDone(upload);

    delete asset['tempUploadingPoster'];
  }

  toggleAutoplay() {
    if (!this.permissionService.canDisplayElement(billingScopes.asset.autoplay)) {
      return this.checkoutService.open(billingScopes.asset.autoplay);
    }
    const asset = this.asset();
    this.assetService.updateAsset(asset, { autoplay: !asset.autoplay }, this.gallery().id, <string>asset.section);
  }
}
